import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

/**
 * Canonical haptics helper for the whole app.
 *
 * - Native (Capacitor) uses real iOS haptics; web falls back to navigator.vibrate.
 * - Every fire is gated by the per-device "Haptics" preference (Settings, default ON).
 * - Every fire is gated by a global suppression flag so background replays
 *   (offline-sync flush) never produce a buzz-storm.
 * - Nothing here ever throws into the UI.
 */

const isNative = (() => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
})();

/* ------------------------------------------------------------------ */
/* Preference (per-device)                                             */
/* ------------------------------------------------------------------ */

const PREF_KEY = 'mpc_haptics_enabled';
const listeners = new Set<(enabled: boolean) => void>();

let enabled = (() => {
  try {
    return localStorage.getItem(PREF_KEY) !== 'false';
  } catch {
    return true;
  }
})();

export function isHapticsEnabled(): boolean {
  return enabled;
}

export function setHapticsEnabled(next: boolean) {
  enabled = next;
  try {
    localStorage.setItem(PREF_KEY, next ? 'true' : 'false');
  } catch {
    /* ignore */
  }
  listeners.forEach((cb) => {
    try {
      cb(next);
    } catch {
      /* ignore */
    }
  });
}

export function subscribeHapticsEnabled(cb: (enabled: boolean) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/* ------------------------------------------------------------------ */
/* Suppression (offline-sync replay guard)                             */
/* ------------------------------------------------------------------ */

let suppressed = false;

/** True while a background replay is running — all haptics no-op. */
export function areHapticsSuppressed(): boolean {
  return suppressed;
}

export function setHapticsSuppressed(next: boolean) {
  suppressed = next;
}

/** Run `fn` with haptics suppressed (used by the offline-sync flush). */
export async function withHapticsSuppressed<T>(fn: () => Promise<T> | T): Promise<T> {
  const prev = suppressed;
  suppressed = true;
  try {
    return await fn();
  } finally {
    suppressed = prev;
  }
}

function allowed(): boolean {
  return enabled && !suppressed;
}

/* ------------------------------------------------------------------ */
/* Primitives                                                          */
/* ------------------------------------------------------------------ */

function nativeImpact(style: ImpactStyle) {
  if (!isNative) return false;
  try {
    // Fire-and-forget — never block the UI on a haptic.
    Haptics.impact({ style }).catch(() => {});
  } catch {
    /* silent */
  }
  return true;
}

function nativeNotification(type: NotificationType) {
  if (!isNative) return false;
  try {
    Haptics.notification({ type }).catch(() => {});
  } catch {
    /* silent */
  }
  return true;
}

function nativeSelection() {
  if (!isNative) return false;
  try {
    Haptics.selectionStart().catch(() => {});
    setTimeout(() => {
      try {
        Haptics.selectionEnd().catch(() => {});
      } catch {
        /* silent */
      }
    }, 30);
  } catch {
    /* silent */
  }
  return true;
}

function webVibrate(pattern: number | number[]) {
  if (typeof navigator === 'undefined' || !('vibrate' in navigator)) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    /* not supported — silent */
  }
}

/* ------------------------------------------------------------------ */
/* Public API                                                          */
/* ------------------------------------------------------------------ */

export const haptics = {
  vibrate(pattern: number | number[] = 10) {
    if (!allowed()) return;
    if (isNative) nativeImpact(ImpactStyle.Light);
    else webVibrate(pattern);
  },
  lightTap() {
    if (!allowed()) return;
    if (!nativeImpact(ImpactStyle.Light)) webVibrate(10);
  },
  mediumTap() {
    if (!allowed()) return;
    if (!nativeImpact(ImpactStyle.Medium)) webVibrate(20);
  },
  heavyTap() {
    if (!allowed()) return;
    if (!nativeImpact(ImpactStyle.Heavy)) webVibrate(40);
  },
  /** Distinct "you just hit a PR" feel — double heavy pulse. */
  prTap() {
    if (!allowed()) return;
    if (isNative) {
      nativeImpact(ImpactStyle.Heavy);
      setTimeout(() => {
        if (allowed()) nativeImpact(ImpactStyle.Heavy);
      }, 90);
    } else {
      webVibrate([35, 60, 35]);
    }
  },
  selection() {
    if (!allowed()) return;
    if (!nativeSelection()) webVibrate(5);
  },
  success() {
    if (!allowed()) return;
    if (!nativeNotification(NotificationType.Success)) webVibrate([10, 50, 10]);
  },
  error() {
    if (!allowed()) return;
    if (!nativeNotification(NotificationType.Error)) webVibrate([50, 30, 50]);
  },
  warning() {
    if (!allowed()) return;
    if (!nativeNotification(NotificationType.Warning)) webVibrate([30, 20, 30]);
  },
};

export type HapticsApi = typeof haptics;

export function useHaptics(): HapticsApi {
  return haptics;
}
